import { sanitiseSubdomain } from '@squaredup/tenants';
import {
    getSharedHostFromAppUrl,
    getSubdomainFromAppUrl,
    isReservedSubdomain
} from 'services/Auth/getApplicationSubdomain';

interface TenantSelectionRedirectParams {
    /** The URL the app is currently loaded on. */
    currentUrl: URL;

    /** The id of the tenant the user has just selected. */
    tenantId: string;

    /** The selected tenant's own configured subdomain, if any. */
    tenantSubdomain: string | undefined;
}

/**
 * Work out whether landing on the selected tenant means moving to a different host, carrying the
 * selection across the origin boundary.
 *
 * Picking a tenant should always take you to that tenant, even if it means leaving the subdomain
 * you're on. When we're on a tenant subdomain that the selected tenant does not belong to, the tenant
 * belongs elsewhere: on its own subdomain if it has one (e.g. `bigcorp.eu.app.squaredup.com` ->
 * `megacorp.eu.app.squaredup.com`), otherwise on the shared/regional domain (e.g.
 * `bigcorp.eu.app.squaredup.com` -> `eu.app.squaredup.com`). The selection is carried in
 * `?preferredTenant=<id>` so the destination origin, whose preferredTenant localStorage is separate,
 * resolves the right tenant without re-prompting.
 *
 * A tenant's own subdomain is its home whether or not it enforces it: if you pick it from the list it
 * makes sense to go there. Enforcement only governs whether a tenant may also be viewed off its
 * subdomain, which is the redirect gate's concern, not this one.
 *
 * Returns null when no cross-origin move is needed: we're not on a subdomain (already on the shared
 * domain or an unrecognised host), or the selected tenant already belongs to the current subdomain.
 */
export const findTenantSelectionRedirectUrl = ({
    currentUrl,
    tenantId,
    tenantSubdomain
}: TenantSelectionRedirectParams): string | null => {
    const currentSubdomain = getSubdomainFromAppUrl(currentUrl);
    if (!currentSubdomain) {
        return null;
    }

    const targetSubdomain = sanitiseSubdomain(tenantSubdomain);
    const hasOwnSubdomain = targetSubdomain ? !isReservedSubdomain(targetSubdomain) : false;

    if (hasOwnSubdomain && targetSubdomain === currentSubdomain) {
        /** The tenant is associated with the subdomain we're already on, so there's nothing to move. */
        return null;
    }

    /** The shared/regional host the subdomain sits in front of, e.g. `eu.app.squaredup.com`. */
    const sharedHost = getSharedHostFromAppUrl(currentUrl);
    if (!sharedHost.includes('.')) {
        /** Not a recognisable app host (e.g. an apex domain), so don't attempt a redirect. */
        return null;
    }

    const targetUrl = new URL(currentUrl.toString());
    targetUrl.hostname = hasOwnSubdomain ? `${targetSubdomain}.${sharedHost}` : sharedHost;
    targetUrl.searchParams.set('preferredTenant', tenantId);

    return targetUrl.toString();
};
